import { jsonParseLinter, json as langJson } from '@codemirror/lang-json';
import { lintGutter, linter } from '@codemirror/lint';
import { type SxProps } from '@mui/material';
import CodeMirror, {
  type BasicSetupOptions,
  EditorSelection,
  EditorView,
} from '@uiw/react-codemirror';
import { useId } from 'react';

import { CopyToClipboardButton, FormField } from 'common';

import {
  defaultCodeMirrorProps,
  getCodeMirrorFormFieldSxProps,
  getCodeMirrorStyles,
} from './defaultProps';
import { useCodeMirrorAutocompleteContext } from '../../../../Code/codeMirrorHooks';
import { useReactFlowViewContext } from '../../../context/context';
import { type ConfigFieldProps } from '../types';
import { useSelectedNodeField } from '../useSelectedNodeField';

export enum LanguageMode {
  Script = 'Script',
  Expression = 'Expression',
  Json = 'Json',
}

interface FieldProps extends ConfigFieldProps {
  label: string;
  autoFocus?: boolean;
  helperText?: string;
  languageMode?: LanguageMode;
  basicSetup?: BasicSetupOptions;
  minHeight?: string;
  maxHeight?: string;
  onChange?: (value: string) => void;
}

interface InnerFieldProps extends FieldProps {
  fieldId: string;
}

export function BlockCodeMirrorInnerField(props: InnerFieldProps) {
  const {
    label,
    fieldName,
    autoFocus,
    fieldId,
    languageMode = LanguageMode.Script,
    basicSetup,
    minHeight,
    maxHeight,
  } = props;
  const { field, value, isFieldErrorVisible, setValue } = useSelectedNodeField<string>(fieldName);
  const { isEditable } = useReactFlowViewContext();
  const { expressionNodeNameExtensions, javascriptNodeNameExtensions } =
    useCodeMirrorAutocompleteContext();

  return (
    <CodeMirror
      id={fieldId}
      aria-label={label}
      autoFocus={autoFocus}
      extensions={[
        EditorView.lineWrapping,
        ...(languageMode === LanguageMode.Json
          ? [langJson(), linter(jsonParseLinter()), lintGutter()]
          : languageMode === LanguageMode.Expression
            ? expressionNodeNameExtensions
            : javascriptNodeNameExtensions),
      ]}
      selection={EditorSelection.cursor(value?.length ?? 0)}
      {...field}
      editable={isEditable}
      onChange={async (value) => {
        if (isEditable) {
          setValue(value);
          props.onChange?.(value);
        }
      }}
      {...defaultCodeMirrorProps}
      basicSetup={basicSetup}
      minHeight={minHeight ?? '120px'}
      maxHeight={maxHeight ?? '40vh'}
      style={getCodeMirrorStyles({
        isFieldErrorVisible,
      })}
    />
  );
}

export function BlockCodeMirrorField(props: FieldProps) {
  const { label, fieldName, helperText } = props;
  const { visibleError, value } = useSelectedNodeField<string>(fieldName);
  const { isEditable } = useReactFlowViewContext();

  const fieldId = useId();
  return (
    <FormField
      id={fieldId}
      label={label}
      error={visibleError}
      sx={getCodeMirrorFormFieldSxProps({ isEditable }) as SxProps}
      helperText={helperText}
      labelAdornment={
        <CopyToClipboardButton animationVariant="AlwaysVisible" content={value || ''} />
      }
    >
      <BlockCodeMirrorInnerField {...props} fieldId={fieldId} />
    </FormField>
  );
}
